import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Presentation, Maximize2, AlertCircle, X, ExternalLink, Pencil } from 'lucide-react';
import {
  getTopicForModule,
  getSubtopicsForTopic,
  buildPresentationUrl,
  renderPresentationHtml,
  parseSlidesSrcs,
  slidesCache,
  RECORTES_HTML_BASE,
} from '../../lib/recortes-html';
import type { TemplateTopic } from '../../types/templates';
import { useAuth } from '../../contexts/AuthContext';
import { SlideEditorModal } from './SlideEditorModal';

interface ModuleHTMLViewerProps {
  moduleId:         string;
  moduleName:       string;
  classNumber?:     number | null;
  docente?:         string | null;
  numeroDiplomado?: string | null;
  totalClases?:     number | null;
}

type State =
  | { kind: 'loading' }
  | { kind: 'no-topic' }
  | { kind: 'descartado'; topic: TemplateTopic }
  | { kind: 'ready'; topic: TemplateTopic; url: string; rawUrl: string; isPlantilla: boolean; slideUrls: string[] }
  | { kind: 'error'; message: string };

export function ModuleHTMLViewer({
  moduleId,
  moduleName,
  classNumber,
  docente,
  numeroDiplomado,
  totalClases,
}: ModuleHTMLViewerProps) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [open, setOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const { profile } = useAuth();
  const canEdit = profile?.role === 'docente' || profile?.role === 'admin';

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    setOpen(false);
    let blobToRevoke: string | null = null;

    (async () => {
      try {
        const topic = await getTopicForModule(moduleId);
        if (cancelled) return;
        if (!topic) { setState({ kind: 'no-topic' }); return; }
        if (topic.status === 'descartado') { setState({ kind: 'descartado', topic }); return; }

        // Siempre PLANTILLA: portada dinámica + índice con subtemas clickables.
        const resolvedUrl = buildPresentationUrl(topic);
        if (!resolvedUrl) { setState({ kind: 'error', message: 'No se pudo construir URL del HTML' }); return; }

        const [subs, res] = await Promise.all([
          getSubtopicsForTopic(topic.id),
          fetch(`${resolvedUrl}?t=${Date.now()}`, { cache: 'no-store' }),
        ]);
        if (cancelled) return;
        if (!res.ok) throw new Error(`HTTP ${res.status} cargando plantilla`);

        // Expandir slides individuales de cada recorte en paralelo con res.text()
        const subsWithRef = subs.filter(s => s.html_ref);
        const [html, slideUrlsPerSub] = await Promise.all([
          res.text(),
          Promise.all(subsWithRef.map(async sub => {
            const rawRef = sub.html_ref!;
            const ref = rawRef.endsWith('.html') ? rawRef : `${rawRef}/index.html`;
            const indexUrl = `${RECORTES_HTML_BASE}/${ref}`;
            // Cache por sesión: si ya se fetcheó este recorte, reusar
            if (slidesCache.has(indexUrl)) return slidesCache.get(indexUrl)!;
            const dir = ref.slice(0, ref.lastIndexOf('/'));
            try {
              const r = await fetch(`${indexUrl}?t=${Date.now()}`, { cache: 'no-store' });
              if (!r.ok) { slidesCache.set(indexUrl, []); return [] as string[]; }
              const h = await r.text();
              const srcs = parseSlidesSrcs(h);
              const urls = srcs.length > 0
                ? srcs.map(s => `${RECORTES_HTML_BASE}/${dir}/${s}`)
                : [];
              slidesCache.set(indexUrl, urls);
              return urls;
            } catch { slidesCache.set(indexUrl, []); return [] as string[]; }
          })),
        ]);
        if (cancelled) return;

        // Calcular offsets: portada=0, índice=1, slides de recortes desde 2
        const allSlideUrls = slideUrlsPerSub.flat();
        let off = 2;
        const cardOffsets = slideUrlsPerSub.map(slides => {
          const o = off;
          off += slides.length;
          return o;
        });

        const tituloCorto = topic.name.split('/')[0].trim();
        const isConcepto  = (numeroDiplomado ?? '').toUpperCase().startsWith('CONCEPTO');
        const unidadNum   = isConcepto ? '' : deriveUnidad(topic.default_class_number);
        const unidadNom   = isConcepto ? '' : deriveNombreUnidad(topic.default_class_number);

        const rendered = renderPresentationHtml(html, {
          numClase:         classNumber ?? topic.default_class_number,
          tituloClase:      tituloCorto,
          subtemas:         subs.map(s => ({
            order:       s.order_in_topic,
            title:       s.title,
            description: s.section_label ?? null,
            htmlRef:     s.html_ref ?? null,
          })),
          docente,
          numeroDiplomado,
          categoria:        tituloCorto,
          subtitle:         topic.description ?? null,
          duracionH:        topic.duracion_h ?? (isConcepto ? 1.5 : 3),
          totalClases:      totalClases ?? (isConcepto ? 30 : 34),
          numeroUnidad:     unidadNum,
          nombreUnidad:     unidadNom,
          htmlBase:         RECORTES_HTML_BASE,
          recorteSlideUrls: allSlideUrls.length > 0 ? allSlideUrls : undefined,
          cardOffsets:      cardOffsets.length > 0 ? cardOffsets : undefined,
        });

        // Inyectar <base> apuntando al directorio del template para que los
        // assets relativos (CSS/JS/img) resuelvan correctamente cuando el blob
        // se abre en una nueva pestaña (fuera del contexto del iframe).
        const templateDir = resolvedUrl.slice(0, resolvedUrl.lastIndexOf('/') + 1);
        const renderedWithBase = /<base\s/i.test(rendered)
          ? rendered
          : rendered.replace(/<head([^>]*)>/i, `<head$1><base href="${templateDir}">`);
        const blob    = new Blob([renderedWithBase], { type: 'text/html;charset=utf-8' });
        const blobUrl = URL.createObjectURL(blob);
        blobToRevoke  = blobUrl;
        setState({ kind: 'ready', topic, url: blobUrl, rawUrl: resolvedUrl, isPlantilla: true, slideUrls: allSlideUrls });
      } catch (e: any) {
        if (!cancelled) setState({ kind: 'error', message: e?.message ?? 'Error cargando presentación' });
      }
    })();

    return () => {
      cancelled = true;
      if (blobToRevoke) URL.revokeObjectURL(blobToRevoke);
    };
  }, [moduleId, classNumber, docente, numeroDiplomado]);

  if (state.kind === 'no-topic') return null;

  return (
    <>
      <TriggerCard
        state={state}
        moduleName={moduleName}
        classNumber={classNumber}
        onOpen={() => setOpen(true)}
      />
      {open && state.kind === 'ready' && (
        <FullScreenViewer
          url={state.url}
          title={`Clase ${classNumber ?? '?'} — ${moduleName}`}
          rawUrl={state.rawUrl}
          slideUrls={state.slideUrls}
          canEdit={canEdit}
          onEdit={() => { setOpen(false); setEditorOpen(true); }}
          onClose={() => setOpen(false)}
        />
      )}
      {editorOpen && state.kind === 'ready' && state.slideUrls.length > 0 && (
        <SlideEditorModal
          slideUrls={state.slideUrls}
          onClose={() => setEditorOpen(false)}
        />
      )}
    </>
  );
}


function deriveUnidad(classNum: number): string {
  if (classNum <= 6)  return '01';
  if (classNum <= 14) return '02';
  if (classNum <= 16) return '03';
  if (classNum <= 21) return '04';
  if (classNum <= 27) return '05';
  if (classNum <= 31) return '06';
  return '07';
}

function deriveNombreUnidad(classNum: number): string {
  if (classNum <= 6)  return 'Fundamentos';
  if (classNum <= 14) return 'Modelado';
  if (classNum <= 16) return 'Vistas';
  if (classNum <= 21) return 'Documentación';
  if (classNum <= 27) return 'Familias';
  if (classNum <= 31) return 'Coordinación';
  return 'Cierre';
}


function TriggerCard({
  state,
  moduleName,
  classNumber,
  onOpen,
}: {
  state:       State;
  moduleName:  string;
  classNumber: number | null | undefined;
  onOpen:      () => void;
}) {
  const isReady   = state.kind === 'ready';
  const isDescart = state.kind === 'descartado';
  const isError   = state.kind === 'error';
  const isLoading = state.kind === 'loading';

  const accent = isReady
    ? 'border-cubus-lime/30 hover:border-cubus-lime/60 hover:bg-cubus-lime/5 cursor-pointer'
    : 'border-white/[0.08] opacity-70 cursor-not-allowed';

  return (
    <div
      className={`glass-deep rounded-lg border transition-all duration-150 ${accent}`}
      onClick={isReady ? onOpen : undefined}
      role={isReady ? 'button' : undefined}
      tabIndex={isReady ? 0 : undefined}
      onKeyDown={e => { if (isReady && (e.key === 'Enter' || e.key === ' ')) onOpen(); }}
    >
      <div className="p-4 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${isReady ? 'bg-cubus-lime/15' : 'bg-white/[0.05]'}`}>
          {isError
            ? <AlertCircle className="w-6 h-6 text-rose-400" />
            : <Presentation className={`w-6 h-6 ${isReady ? 'text-cubus-lime' : 'text-muted'}`} />
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[2px] uppercase font-semibold text-cubus-lime mb-0.5">
            Presentación interactiva
            {classNumber != null && (
              <span className="text-muted/60 normal-case tracking-normal font-normal ml-2">
                · Clase {String(classNumber).padStart(2, '0')}
              </span>
            )}
          </div>
          <h4 className="text-sm font-medium text-white truncate">{moduleName}</h4>
          <p className="text-xs text-muted mt-0.5">
            {isLoading && 'Cargando…'}
            {isReady   && 'Click para abrir en pantalla completa'}
            {isDescart && 'Sin presentación HTML asociada'}
            {isError   && `Error: ${(state as any).message}`}
          </p>
        </div>
        {isReady && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-cubus-lime hidden sm:inline">Abrir</span>
            <div className="w-9 h-9 rounded-md bg-cubus-lime/10 flex items-center justify-center">
              <Maximize2 className="w-4 h-4 text-cubus-lime" />
            </div>
          </div>
        )}
      </div>
      {isError && (
        <div className="px-4 pb-3 text-[11px] text-faint">
          Server: <code className="text-muted">{RECORTES_HTML_BASE}</code> — verificá que esté corriendo (servir-htmls-local.bat).
        </div>
      )}
    </div>
  );
}


function FullScreenViewer({
  url, title, rawUrl, slideUrls, canEdit, onEdit, onClose,
}: {
  url: string; title: string; rawUrl: string;
  slideUrls?: string[]; canEdit?: boolean;
  onEdit?: () => void; onClose: () => void;
}) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={title}
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08] bg-cubus-deep/80 backdrop-blur flex-shrink-0">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[2px] uppercase font-semibold text-cubus-lime mb-0.5">Presentación</div>
          <h2 className="text-sm font-medium text-white truncate max-w-[60vw]">{title}</h2>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && slideUrls && slideUrls.length > 0 && (
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cubus-lime/10 hover:bg-cubus-lime/20 border border-cubus-lime/40 hover:border-cubus-lime text-cubus-lime transition-all"
              title="Editar slides (solo docentes/admin)"
            >
              <Pencil className="w-4 h-4" />
              <span className="text-sm hidden sm:inline">Editar</span>
            </button>
          )}
          <a href={url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 hover:border-cubus-lime/40 text-white/80 transition-all"
            title="Abrir presentación en nueva pestaña">
            <ExternalLink className="w-4 h-4" />
            <span className="text-sm hidden sm:inline">Nueva pestaña</span>
          </a>
          <button onClick={onClose}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 hover:border-cubus-lime/40 text-white/80 transition-all"
            title="Cerrar (Esc)" aria-label="Cerrar">
            <span className="text-sm">Cerrar</span>
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <iframe src={url} title={title} className="flex-1 w-full border-0 block bg-cubus-bg"
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" />
    </div>,
    document.body,
  );
}
